import { cn } from "@/lib/utils";
import { ChartTooltip } from "@backpac/monica";
import { sum } from "es-toolkit";
import React from "react";
import { Area } from "recharts";
import type * as RechartsPrimitive from "recharts";
import type { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BaseChart } from "./base-chart";

// Define the data structure for a single data point in the stacked area chart
type StackedDataPoint = {
  date: string;
  [key: string]: string | number; // Allow for dynamic data keys
};

export type SeriesConfig = {
  dataKey: string;
  name: string;
  color: string;
};

export function StackedAreaChart(props: {
  data: StackedDataPoint[];
  series: SeriesConfig[];
  formatValue: (value: number) => string;
  dateType?: "month" | "day";
}) {
  const { data, series, formatValue, dateType = "day" } = props;

  return (
    <BaseChart data={data} formatValue={formatValue} dateType={dateType}>
      <ChartTooltip
        cursor={{
          stroke: "#0F0F0F",
          strokeDasharray: "2 2",
        }}
        content={
          <StackedChartTooltipContent
            formatLabel={(value: string) =>
              dateType === "day"
                ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                : new Date(value).toLocaleDateString("en-US", { month: "short", year: "numeric" })
            }
            formatValue={(value) => formatValue(value as number)}
            series={series}
          />
        }
      />

      <defs>
        {series.map((item) => (
          <linearGradient key={item.name} id={item.name} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={item.color} stopOpacity={0.2} />
            <stop offset="60%" stopColor={item.color} stopOpacity={0} />
          </linearGradient>
        ))}
      </defs>
      {series.map((item) => (
        <Area
          key={item.dataKey}
          dataKey={item.dataKey}
          stackId="1"
          type="monotone"
          name={item.name}
          stroke={item.color}
          fill={item.color}
        />
      ))}
    </BaseChart>
  );
}

// Custom tooltip content component for stacked area chart
const StackedChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      series: SeriesConfig[];
      formatLabel?: (value: string) => string;
      formatValue?: (value: ValueType) => ValueType;
    }
>(({ active, payload, className, formatLabel = (x) => x, formatValue = (x) => x, series }, ref) => {
  if (!active || !payload?.length) {
    return null;
  }

  const label = payload[0]?.payload?.date;

  return (
    <div ref={ref} className={cn("rounded-sm bg-black px-3 py-2 opacity-80", className)}>
      <div className="flex flex-row justify-between mb-1.5">
        <div className="mb-1 text-xs font-medium text-white">{formatLabel(label)}</div>
        <span className="text-xs font-semibold tabular-nums text-white">
          {formatValue(sum(payload.map((p) => p.value as number)))}
        </span>
      </div>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={`item-${index}`} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-xs text-white">
                {series.find((s) => s.dataKey === entry.dataKey)?.name || entry.dataKey}
              </span>
            </div>
            <span className="text-xs font-medium tabular-nums text-white">{formatValue(entry.value as number)}</span>
          </div>
        ))}
      </div>
    </div>
  );
});
